import { LoadingPage } from "gui/loading/page";

import { useRouter } from "next/router";
import { useContext, useMemo } from "react";
import { SessionContext } from "gui/session/session";

import { isServerSide } from "lib/env/env";
import { useLoginUrl } from "lib/hooks/useLoginUrl";

import type { SessionWithUser } from "lib/users/types";

/*
 * For convenience, `behindLogin` passes the session to the component
 * it wraps
 */
export interface LoggedInProps {
  session: SessionWithUser;
}

/*
 * Therefore, when wrapping a page component in behindLogin,
 * getStaticProps should get all the page component's other props, but
 * not session.
 */
export type PropsWithoutSession<Props> = Omit<Props, "session">;

/*
 * Wrap a page component in behindLogin to make it accessible only to
 * logged in users.
 *
 * - Renders a loading page while fetching session.
 * - Redirects to login if user not already logged in.
 * - Renders the page component if logged in.
 */
export default function behindLogin<Props>(
  Page: React.FC<Props & Partial<LoggedInProps>>
): React.FC<Props> {
  return function PageBehindLogin(props: Props) {
    const router = useRouter();
    const loginUrl = useLoginUrl();
    const { session, sessionLoading } = useContext(SessionContext);

    const sessionWithUser: SessionWithUser | undefined = useMemo(() => {
      if (session?.user) {
        return { ...session, user: session.user };
      } else {
        return undefined;
      }
    }, [session]);

    if (sessionLoading || isServerSide() || !router.isReady) {
      // Loading
      return <LoadingPage />;
    }

    if (!sessionWithUser) {
      // Logged out
      router.replace(loginUrl);

      // (Show loading page until we get redirected)
      return <LoadingPage />;
    }

    // Logged in
    return <Page {...props} session={sessionWithUser} />;
  };
}
